import { useState, useMemo } from "react";
import { Layout } from "../components/Layout";
import { StudioBudgetModal } from "../components/StudioBudgetModal";
import {
  STUDIO_PROJECTS, STUDIO_TABS, STUDIO_SERVICES, STUDIO_HERO_IMG, waLink,
} from "../data/studioProjects";
import {
  Check, Eye, ArrowRight, Search, MessageCircle, Monitor, Smartphone, Zap,
  Users, ShieldCheck, Globe, Calendar, ShoppingCart, FileText, Sparkles, LayoutGrid, X,
} from "lucide-react";

const FEATURES = [
  { icon: Monitor, title: "Modern Design", sub: "Clean & Professional" },
  { icon: Zap, title: "Built for Results", sub: "More Customers" },
  { icon: Smartphone, title: "Web & Mobile", sub: "All Devices" },
  { icon: Users, title: "For Real Businesses", sub: "Local & Global" },
  { icon: ShieldCheck, title: "Launch Faster", sub: "Ideas Into Reality" },
];

const SERVICE_ICONS = [Globe, Calendar, ShoppingCart, Smartphone, Users, FileText, Sparkles];

export default function Apps() {
  const [tab, setTab] = useState("All");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState({ open: false, ref: "" });
  const [preview, setPreview] = useState(null);

  const openProject = (p) => setModal({ open: true, ref: p ? `${p.name} — ${p.type}` : "" });

  const filtered = useMemo(
    () =>
      STUDIO_PROJECTS.filter((p) => {
        if (tab !== "All" && p.category !== tab) return false;
        if (q && !`${p.name} ${p.type}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
      }),
    [tab, q]
  );

  return (
    <Layout>
      {/* Hero */}
      <section className="border-b border-gray-100 bg-gradient-to-b from-[#FBF7EA]/60 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.3em] text-[#B8901F] mb-3">LAUNCHBS</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0F0F0F]">
              Web & App <span className="text-[#D4AF37]">Studios</span>
            </h1>
            <p className="font-display text-xl md:text-2xl font-bold text-[#0F0F0F] mt-3">
              See What We Can Build For Your Business.
            </p>
            <p className="text-[#687280] mt-3 text-base leading-relaxed max-w-xl">
              Explore professional websites, booking systems, mobile apps and digital solutions
              designed for real businesses. Your vision. Our build.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <button onClick={() => openProject(null)} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="hero-start-project-btn">
                <Sparkles size={16} /> Start Your Project
              </button>
              <a href={waLink("Hi LaunchBS, I'd like to chat about a website or app for my business.")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-gray-200 rounded-full px-6 py-3 text-sm font-semibold text-[#374151] hover:border-[#D4AF37] transition-colors" data-testid="hero-whatsapp-btn">
                <MessageCircle size={16} className="text-[#25D366]" /> Chat With LaunchBS
              </a>
            </div>
          </div>
          <div className="relative">
            <img src={STUDIO_HERO_IMG} alt="Web & App Studios" className="w-full rounded-2xl shadow-xl" />
            <p className="hidden md:block absolute -right-2 -bottom-4 font-display italic text-[#D4AF37] text-lg font-bold rotate-[-6deg]">
              Ideas. Websites. Apps. Real Results.
            </p>
          </div>
        </div>

        {/* Feature row */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-10">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center shrink-0"><f.icon size={17} /></div>
                <div>
                  <p className="text-sm font-bold text-[#0F0F0F] leading-tight">{f.title}</p>
                  <p className="text-xs text-[#9aa1ab]">{f.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tabs + search */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {STUDIO_TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border transition-colors ${
                  tab === t ? "bg-[#0F0F0F] text-white border-[#0F0F0F]" : "bg-white text-[#374151] border-gray-200 hover:border-[#D4AF37]"
                }`}
                data-testid={`studio-tab-${t.toLowerCase()}`}
              >
                {t === "All" ? <LayoutGrid size={15} /> : t === "Websites" ? <Monitor size={15} /> : <Smartphone size={15} />}
                {t}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa1ab]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search examples..." className="w-full rounded-full border border-gray-200 pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]" data-testid="studio-search" />
          </div>
        </div>
      </section>

      {/* Cards */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-[#687280]">No examples match your search.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.05)] overflow-hidden flex flex-col" data-testid={`studio-card-${p.id}`}>
                <div className="relative bg-[#F7F7F7] h-52 flex items-center justify-center overflow-hidden">
                  <img src={p.image} alt={p.name} className={p.portrait ? "h-full w-auto object-contain py-3" : "w-full h-full object-cover"} />
                  <span className="absolute top-3 left-3 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-[11px] font-bold text-[#0F0F0F] shadow">
                    {p.category === "Apps" ? "App" : "Website"}
                  </span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-display text-lg font-bold text-[#0F0F0F]">{p.name}</h3>
                  <p className="text-sm text-[#687280]">{p.type}</p>
                  <ul className="mt-3 space-y-1.5 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-[#374151]">
                        <span className="w-4 h-4 rounded-full bg-[#D4AF37] flex items-center justify-center shrink-0"><Check size={11} className="text-[#0F0F0F]" /></span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => p.exampleUrl ? window.open(p.exampleUrl, "_blank", "noopener,noreferrer") : p.exampleImage ? setPreview(p) : openProject(p)} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-gray-200 rounded-full py-2.5 text-sm font-semibold text-[#374151] hover:border-[#D4AF37] transition-colors" data-testid={`view-example-${p.id}`}>
                      <Eye size={15} /> View Example
                    </button>
                    <button onClick={() => openProject(p)} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#D4AF37] text-[#0F0F0F] rounded-full py-2.5 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid={`get-built-${p.id}`}>
                      Get This Built <ArrowRight size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* What Can We Build For You */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0F0F0F] text-center">What Can We Build For You?</h2>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          {STUDIO_SERVICES.map((s, i) => {
            const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
            return (
              <div key={s} className="rounded-2xl border border-gray-100 bg-white p-4 text-center shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
                <div className="w-10 h-10 rounded-xl bg-[#FBF7EA] text-[#B8901F] flex items-center justify-center mx-auto"><Icon size={19} /></div>
                <p className="text-[13px] font-semibold text-[#0F0F0F] mt-2 leading-tight">{s}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Dark CTA band */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="rounded-3xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-8 md:p-12">
          <p className="text-xs font-bold tracking-[0.25em] text-[#D4AF37] mb-2">LET'S BUILD YOUR VISION</p>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold">Need a <span className="text-[#D4AF37]">Website or App?</span></h2>
          <p className="text-[#c9c39f] mt-3 max-w-2xl">From simple websites to advanced apps, we create digital solutions that help real businesses grow.</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="inline-flex items-center gap-2 border border-white/30 rounded-full px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors" data-testid="browse-all-btn">
              Browse All Projects
            </a>
            <button onClick={() => openProject(null)} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="cta-start-project-btn">
              <Sparkles size={16} /> Start Your Project
            </button>
            <a href={waLink("Hi LaunchBS, I'm interested in getting help with a website or app for my business.")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-6 py-3 text-sm font-bold hover:bg-[#1ebe5b] transition-colors" data-testid="cta-whatsapp-btn">
              <MessageCircle size={16} /> Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Your Business Could Be Next */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20 text-center">
        <h2 className="font-display text-2xl md:text-4xl font-extrabold tracking-tight text-[#0F0F0F]">YOUR BUSINESS COULD BE NEXT.</h2>
        <p className="text-[#687280] mt-3">Tell us what you need. We'll show you what's possible.</p>
        <button onClick={() => openProject(null)} className="mt-6 inline-flex items-center gap-2 bg-[#0F0F0F] text-white rounded-full px-8 py-3.5 text-sm font-bold hover:bg-[#1c1c1c] transition-colors" data-testid="final-start-project-btn">
          START YOUR PROJECT <ArrowRight size={16} />
        </button>
      </section>

      <StudioBudgetModal open={modal.open} onClose={() => setModal({ open: false, ref: "" })} projectRef={modal.ref} />

      {/* Example preview lightbox */}
      {preview && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8"
          onClick={() => setPreview(null)}
          data-testid="example-preview-overlay"
        >
          <div className="relative w-full max-w-4xl my-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-white">
                <p className="font-display text-lg font-bold leading-tight">{preview.name}</p>
                <p className="text-sm text-white/60">{preview.type}</p>
              </div>
              <button
                onClick={() => setPreview(null)}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors shrink-0"
                data-testid="example-preview-close"
                aria-label="Close preview"
              >
                <X size={20} />
              </button>
            </div>
            <div className="rounded-2xl overflow-hidden bg-white shadow-2xl">
              <img src={preview.exampleImage} alt={`${preview.name} website example`} className="w-full h-auto block" data-testid="example-preview-image" />
            </div>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              <button
                onClick={() => { const p = preview; setPreview(null); openProject(p); }}
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors"
                data-testid="example-preview-get-built"
              >
                Get This Built <ArrowRight size={15} />
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
